import { useEffect, useRef, useState } from 'react';

const types = [
  {
    id: 'yogusmal',
    icon: 'ri-fire-line',
    title: 'Yoğuşmalı Kombi',
    badge: 'En Çok Tercih',
    badgeColor: 'text-primary bg-[#f0f7ff] border-[#dbeafe]',
    desc: 'Yanma sırasında ortaya çıkan su buharını tekrar enerjiye dönüştürerek yüksek verimlilik sağlar. Enerji tasarrufu isteyen kullanıcılar için ideal tercih.',
    features: ['Düşük doğalgaz tüketimi', 'Yüksek enerji verimliliği', 'Çevre dostu çalışma', 'Uzun vadede ekonomik'],
    image: '/images/boiler-vaillant-condensing-v3.jpg',
  },
  {
    id: 'tam-yogusmal',
    icon: 'ri-flashlight-line',
    title: 'Tam Yoğuşmalı Kombi',
    badge: 'Maksimum Tasarruf',
    badgeColor: 'text-primary bg-[#f0f7ff] border-[#dbeafe]',
    desc: 'Yoğuşma teknolojisini en verimli şekilde kullanan gelişmiş cihazlardır. Düşük sıcaklıklarda bile yüksek performans sunarak maksimum tasarruf sağlar.',
    features: ['Büyük metrekareli evler için ideal', 'Düşük sıcaklıkta yüksek performans', 'Maksimum enerji tasarrufu', 'Uzun ömürlü kullanım'],
    image: '/images/boiler-bosch-fullcond-v3.jpg',
  },
  {
    id: 'hermetik',
    icon: 'ri-shield-check-line',
    title: 'Hermetik Kombi',
    badge: 'Güvenli Kullanım',
    badgeColor: 'text-primary bg-[#f0f7ff] border-[#dbeafe]',
    desc: 'Yanma için gerekli havayı dış ortamdan alır ve atık gazı dışarıya tahliye eder. Kapalı yanma odasına sahip olduğu için güvenli kullanım sunar.',
    features: ['Kapalı yanma odası', 'Güvenli gaz tahliyesi', 'Montaj kolaylığı', 'Ekonomik fiyat avantajı'],
    image: '/images/boiler-ariston-hermetic-v3.jpg',
  },
  {
    id: 'konvansiyonel',
    icon: 'ri-home-gear-line',
    title: 'Konvansiyonel (Bacalı) Kombi',
    badge: 'Eski Nesil',
    badgeColor: 'text-slate-600 bg-slate-100 border-slate-200',
    desc: 'Bacalı sistem ile çalışan eski nesil kombi modelleridir. Günümüzde yerini büyük oranda yoğuşmalı kombilere bırakmıştır.',
    features: ['Bacalı sistem', 'Düşük enerji verimliliği', 'Eski yapılarda kullanım', 'Bakım gerektiren sistem'],
    image: '/images/boiler-demirdokum-conv-v3.jpg',
  },
];

export default function BoilerTypesSection() {
  const ref = useRef<HTMLElement>(null);
  const [active, setActive] = useState('yogusmal');
  const [loadedImages, setLoadedImages] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add('opacity-100', 'translate-y-0');
          e.target.classList.remove('opacity-0', 'translate-y-6');
        }
      }),
      { threshold: 0.1 }
    );
    ref.current?.querySelectorAll('.anim').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const handleImageLoad = (id: string) => {
    setLoadedImages((prev) => ({ ...prev, [id]: true }));
  };

  const activeType = types.find((t) => t.id === active) ?? types[0];

  return (
    <section id="boiler-types" ref={ref} className="w-full py-16 md:py-24 bg-white">
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="anim opacity-0 translate-y-6 transition-all duration-500 text-center mb-12">
          <span className="inline-flex items-center gap-2 text-xs font-bold text-primary bg-[#f0f7ff] border border-[#dbeafe] px-3 py-1.5 rounded-full uppercase tracking-widest mb-4">
            Kombi Çeşitleri
          </span>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-3 tracking-tight">
            Kombi Modelleri ve Çeşitleri
          </h2>
          <p className="text-sm text-slate-500 max-w-xl mx-auto">
            Doğru kombi seçimi hem enerji tasarrufu sağlar hem de uzun vadede konforlu kullanım sunar. Uzman ekibimiz, ihtiyacınıza en uygun modeli belirlemenize yardımcı olur.
          </p>
        </div>

        {/* Tabs */}
        <div className="anim opacity-0 translate-y-6 transition-all duration-500 flex flex-wrap gap-2 justify-center mb-8">
          {types.map((t) => (
            <button
              key={t.id}
              onClick={() => setActive(t.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold border transition-all whitespace-nowrap cursor-pointer ${
                active === t.id
                  ? 'bg-primary text-white border-primary'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-primary/30 hover:text-primary'
              }`}
            >
              <i className={`${t.icon} text-base`} />
              {t.title}
            </button>
          ))}
        </div>

        {/* Active card — sol görsel, sağ içerik */}
        <div className="anim opacity-0 translate-y-6 transition-all duration-500 bg-[#f0f7ff] rounded-3xl border border-[#dbeafe] overflow-hidden">
          <div className="flex flex-col md:flex-row">

            {/* Sol: Tüm görseller render edilmiş, sadece aktif olan görünür */}
            <div className="w-full md:w-[320px] flex-shrink-0 h-[260px] md:h-auto bg-white relative overflow-hidden">
              {types.map((t) => (
                <img
                  key={t.id}
                  src={t.image}
                  alt={t.title}
                  onLoad={() => handleImageLoad(t.id)}
                  className={`absolute inset-0 w-full h-full object-contain object-center p-6 transition-opacity duration-300 ${
                    active === t.id
                      ? 'opacity-100'
                      : 'opacity-0 pointer-events-none'
                  }`}
                  loading={t.id === 'yogusmal' ? 'eager' : 'lazy'}
                  decoding="async"
                  fetchPriority={t.id === 'yogusmal' ? 'high' : 'low'}
                />
              ))}
              {/* Yükleniyor göstergesi */}
              {!loadedImages[active] && (
                <div className="absolute inset-0 flex items-center justify-center bg-white">
                  <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
              )}
            </div>

            {/* Sağ: İçerik */}
            <div className="flex-1 p-8 md:p-10 flex flex-col justify-between">
              <div>
                {/* Başlık */}
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-11 h-11 flex items-center justify-center bg-primary/[0.08] text-primary rounded-xl">
                    <i className={`${activeType.icon} text-xl`} />
                  </div>
                  <div>
                    <span className={`text-[11px] font-bold border px-2 py-0.5 rounded-full ${activeType.badgeColor}`}>
                      {activeType.badge}
                    </span>
                    <h3 className="text-xl font-extrabold text-slate-900 mt-0.5">{activeType.title}</h3>
                  </div>
                </div>

                {/* Açıklama */}
                <p className="text-sm text-slate-600 leading-relaxed mb-6">{activeType.desc}</p>

                {/* Özellikler */}
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mb-8">
                  {activeType.features.map((f) => (
                    <li key={f} className="flex items-center gap-2.5 text-sm text-slate-700">
                      <i className="ri-checkbox-circle-fill text-primary flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Alt: Buton */}
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href="tel:05332836679"
                  className="inline-flex items-center justify-center gap-2 bg-primary hover:bg-primary-dark text-white font-bold text-sm px-6 py-3 rounded-full transition-colors whitespace-nowrap"
                >
                  <i className="ri-phone-fill" />
                  Ücretsiz Danışın
                </a>
                <a
                  href="https://wa.me/905332836679"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-white border border-slate-200 hover:border-primary/30 text-slate-700 hover:text-primary font-semibold text-sm px-6 py-3 rounded-full transition-colors whitespace-nowrap"
                >
                  <i className="ri-whatsapp-line text-green-500" />
                  WhatsApp&apos;tan Yaz
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}